/**
 * Permission catalogue.
 *
 * The authoritative role→permission mapping lives in the DATABASE (Role,
 * Permission, RolePermission), so an operator can adjust it without a deploy.
 * What lives here is:
 *   1. the canonical list of permission keys, so code has type-safe constants;
 *   2. the DEFAULT mapping used to seed those tables.
 *
 * Runtime checks always read the DB (via @musleempay/auth), never this file.
 */
export const PERMISSIONS = {
    PRODUCTS_VIEW: 'products.view',
    PRODUCTS_CREATE: 'products.create',
    PRODUCTS_UPDATE: 'products.update',
    PRODUCTS_DELETE: 'products.delete',
    ORDERS_VIEW: 'orders.view',
    ORDERS_UPDATE: 'orders.update',
    ORDERS_REFUND: 'orders.refund',
    ORDERS_CANCEL: 'orders.cancel',
    CUSTOMERS_VIEW: 'customers.view',
    CUSTOMERS_CREATE: 'customers.create',
    CUSTOMERS_UPDATE: 'customers.update',
    CUSTOMERS_DELETE: 'customers.delete',
    COUPONS_VIEW: 'coupons.view',
    COUPONS_CREATE: 'coupons.create',
    COUPONS_UPDATE: 'coupons.update',
    COUPONS_DELETE: 'coupons.delete',
    SALES_PAGES_VIEW: 'sales_pages.view',
    SALES_PAGES_CREATE: 'sales_pages.create',
    SALES_PAGES_UPDATE: 'sales_pages.update',
    SALES_PAGES_PUBLISH: 'sales_pages.publish',
    SALES_PAGES_DELETE: 'sales_pages.delete',
    PAYMENTS_VIEW: 'payments.view',
    PAYMENTS_REFUND: 'payments.refund',
    INVOICES_VIEW: 'invoices.view',
    REPORTS_VIEW: 'reports.view',
    SHIPMENTS_VIEW: 'shipments.view',
    SHIPMENTS_MANAGE: 'shipments.manage',
    STAFF_VIEW: 'staff.view',
    STAFF_MANAGE: 'staff.manage',
    SETTINGS_VIEW: 'settings.view',
    SETTINGS_MANAGE: 'settings.manage',
    AUDIT_LOGS_VIEW: 'audit_logs.view',
};
export const ALL_PERMISSIONS = Object.values(PERMISSIONS);
/** Grouping used to render the permission matrix in the staff UI. */
export const PERMISSION_GROUPS = {
    Products: [
        PERMISSIONS.PRODUCTS_VIEW,
        PERMISSIONS.PRODUCTS_CREATE,
        PERMISSIONS.PRODUCTS_UPDATE,
        PERMISSIONS.PRODUCTS_DELETE,
    ],
    Orders: [
        PERMISSIONS.ORDERS_VIEW,
        PERMISSIONS.ORDERS_UPDATE,
        PERMISSIONS.ORDERS_REFUND,
        PERMISSIONS.ORDERS_CANCEL,
    ],
    Customers: [
        PERMISSIONS.CUSTOMERS_VIEW,
        PERMISSIONS.CUSTOMERS_CREATE,
        PERMISSIONS.CUSTOMERS_UPDATE,
        PERMISSIONS.CUSTOMERS_DELETE,
    ],
    Coupons: [
        PERMISSIONS.COUPONS_VIEW,
        PERMISSIONS.COUPONS_CREATE,
        PERMISSIONS.COUPONS_UPDATE,
        PERMISSIONS.COUPONS_DELETE,
    ],
    'Sales pages': [
        PERMISSIONS.SALES_PAGES_VIEW,
        PERMISSIONS.SALES_PAGES_CREATE,
        PERMISSIONS.SALES_PAGES_UPDATE,
        PERMISSIONS.SALES_PAGES_PUBLISH,
        PERMISSIONS.SALES_PAGES_DELETE,
    ],
    Payments: [PERMISSIONS.PAYMENTS_VIEW, PERMISSIONS.PAYMENTS_REFUND, PERMISSIONS.INVOICES_VIEW],
    Fulfilment: [PERMISSIONS.SHIPMENTS_VIEW, PERMISSIONS.SHIPMENTS_MANAGE],
    Insights: [PERMISSIONS.REPORTS_VIEW, PERMISSIONS.AUDIT_LOGS_VIEW],
    Administration: [
        PERMISSIONS.STAFF_VIEW,
        PERMISSIONS.STAFF_MANAGE,
        PERMISSIONS.SETTINGS_VIEW,
        PERMISSIONS.SETTINGS_MANAGE,
    ],
};
/** Short description rendered next to each permission in the UI. */
export const PERMISSION_DESCRIPTIONS = {
    'products.view': 'View products and stock levels',
    'products.create': 'Create new products',
    'products.update': 'Edit products, pricing and stock',
    'products.delete': 'Archive or delete products',
    'orders.view': 'View orders and their history',
    'orders.update': 'Update order status and fulfilment details',
    'orders.refund': 'Issue refunds against orders',
    'orders.cancel': 'Cancel orders',
    'customers.view': 'View customer records',
    'customers.create': 'Add customers manually',
    'customers.update': 'Edit customer details, tags and notes',
    'customers.delete': 'Delete customer records',
    'coupons.view': 'View coupons',
    'coupons.create': 'Create coupons',
    'coupons.update': 'Edit coupons',
    'coupons.delete': 'Delete coupons',
    'sales_pages.view': 'View sales pages',
    'sales_pages.create': 'Create sales pages',
    'sales_pages.update': 'Edit sales page content',
    'sales_pages.publish': 'Publish and unpublish sales pages',
    'sales_pages.delete': 'Delete sales pages',
    'payments.view': 'View payment transactions',
    'payments.refund': 'Refund payments directly',
    'invoices.view': 'View and download invoices',
    'reports.view': 'View sales reports and analytics',
    'shipments.view': 'View shipments and tracking',
    'shipments.manage': 'Create and update shipments',
    'staff.view': 'View staff members',
    'staff.manage': 'Invite, edit and remove staff',
    'settings.view': 'View store settings',
    'settings.manage': 'Change store settings',
    'audit_logs.view': 'View the audit trail',
};
const P = PERMISSIONS;
/**
 * Default role → permission mapping, used by the seed script to populate
 * RolePermission. Chosen so each role can do its job and nothing more.
 */
export const DEFAULT_ROLE_PERMISSIONS = {
    /** Full control, including ownership-level settings. */
    OWNER: ALL_PERMISSIONS,
    /** Everything except the audit trail is available to admins. */
    ADMIN: ALL_PERMISSIONS.filter((p) => p !== P.AUDIT_LOGS_VIEW),
    /** Runs day-to-day trading but cannot manage staff or refund money. */
    MANAGER: [
        P.PRODUCTS_VIEW,
        P.PRODUCTS_CREATE,
        P.PRODUCTS_UPDATE,
        P.PRODUCTS_DELETE,
        P.ORDERS_VIEW,
        P.ORDERS_UPDATE,
        P.ORDERS_CANCEL,
        P.CUSTOMERS_VIEW,
        P.CUSTOMERS_CREATE,
        P.CUSTOMERS_UPDATE,
        P.COUPONS_VIEW,
        P.COUPONS_CREATE,
        P.COUPONS_UPDATE,
        P.COUPONS_DELETE,
        P.SALES_PAGES_VIEW,
        P.SALES_PAGES_CREATE,
        P.SALES_PAGES_UPDATE,
        P.SALES_PAGES_PUBLISH,
        P.PAYMENTS_VIEW,
        P.INVOICES_VIEW,
        P.SHIPMENTS_VIEW,
        P.SHIPMENTS_MANAGE,
        P.REPORTS_VIEW,
        P.SETTINGS_VIEW,
        P.STAFF_VIEW,
    ],
    /** Sells: sees the catalogue, works orders and customers. */
    SALES: [
        P.PRODUCTS_VIEW,
        P.ORDERS_VIEW,
        P.ORDERS_UPDATE,
        P.CUSTOMERS_VIEW,
        P.CUSTOMERS_CREATE,
        P.CUSTOMERS_UPDATE,
        P.COUPONS_VIEW,
        P.SALES_PAGES_VIEW,
        P.REPORTS_VIEW,
    ],
    /** Money: payments, refunds, invoices and reporting. */
    FINANCE: [
        P.ORDERS_VIEW,
        P.ORDERS_REFUND,
        P.PAYMENTS_VIEW,
        P.PAYMENTS_REFUND,
        P.INVOICES_VIEW,
        P.CUSTOMERS_VIEW,
        P.PRODUCTS_VIEW,
        P.REPORTS_VIEW,
        P.SETTINGS_VIEW,
    ],
    /** Fulfilment: stock and shipping only, no pricing or customer editing. */
    WAREHOUSE: [
        P.PRODUCTS_VIEW,
        P.PRODUCTS_UPDATE,
        P.ORDERS_VIEW,
        P.ORDERS_UPDATE,
        P.SHIPMENTS_VIEW,
        P.SHIPMENTS_MANAGE,
    ],
    /** Support: reads almost everything, changes customer-facing details. */
    CUSTOMER_SERVICE: [
        P.PRODUCTS_VIEW,
        P.ORDERS_VIEW,
        P.ORDERS_UPDATE,
        P.CUSTOMERS_VIEW,
        P.CUSTOMERS_UPDATE,
        P.COUPONS_VIEW,
        P.INVOICES_VIEW,
        P.SHIPMENTS_VIEW,
    ],
};
/** Human labels for each merchant role. */
export const ROLE_LABELS = {
    OWNER: 'Owner',
    ADMIN: 'Administrator',
    MANAGER: 'Manager',
    SALES: 'Sales',
    FINANCE: 'Finance',
    WAREHOUSE: 'Warehouse',
    CUSTOMER_SERVICE: 'Customer service',
};
export const ROLE_DESCRIPTIONS = {
    OWNER: 'Full access to the store, including billing and ownership.',
    ADMIN: 'Full operational access, including staff and settings.',
    MANAGER: 'Runs the catalogue, orders and campaigns day to day.',
    SALES: 'Works orders and customers; cannot change pricing or settings.',
    FINANCE: 'Handles payments, refunds, invoices and reporting.',
    WAREHOUSE: 'Picks, packs and ships orders; manages stock levels.',
    CUSTOMER_SERVICE: 'Answers customer queries and updates order details.',
};
//# sourceMappingURL=permissions.js.map